import { useSearchParams } from 'react-router-dom'

import { useTranslation } from 'react-i18n-lite'

import {
  Button,
  DocumentListCard,
  IconButton,
  SyntheticButton,
  feedbackcard,
  useDisplayUserError
} from '@yes.technology/react-toolkit'

import { useSelectAndLinkIframeBridge } from 'site/Renderer/components/SelectAndLink'

import useSelection from '../../hooks/useSelection'
import useTableActionSave from '../../hooks/useTableActionSave/useTableActionSave'
import useTableContext from '../../hooks/useTableContext'

import InsertButton from './InsertButton'

import InformationalDocumentUpload from 'component/InformationalDocumentUpload/InformationalDocumentUpload'

import { useMemo, useRef } from 'react'
import { useIsOpen, useParentOverflowDetection } from 'shared/hooks'
import { SelectAndLinkValue } from 'site/Renderer/components/SelectAndLink/SelectAndLink.types'
import { useSiteState } from 'siteState/shared'
import { SelectionAction } from '../../types'
import SelectAndLinkButton from './SelectAndLinkButton'
import {
  ActionsList,
  ButtonContainer,
  ButtonList,
  Container,
  IconWrapper
} from './TableActions.styles'
import { TableActionsProps } from './TableActions.types'

// hide select and deselect fields until implementation
const SHOW_SELECT_DESELECT = false

const TableActions = ({
  initialStatus,
  tableContainerRef
}: TableActionsProps) => {
  const { selectAllRows, selectedRows, deselectAllRows } = useSelection()
  const { t } = useTranslation()
  const {
    mode,
    setMode,
    setChangedFieldValues,
    primarySelectionAction,
    hideEditAction,
    extraSelectionActions,
    disableLinkButton,
    insertOnly,
    hideInsertAction,
    hideSelectAndLinkAction,
    objectclass,
    objectClassTitle,
    selectAndLinkOptions,
    selectAndLinkTargetPath,
    hideDocumentUploadAction
  } = useTableContext()

  const isDocumentObjectClass =
    objectclass?.uuid_objectclass === globalThis.idObjectclassDocument

  const shouldRenderDocumentUploadAction =
    !hideDocumentUploadAction && isDocumentObjectClass
  const buttonContainerRef = useRef<HTMLDivElement>(null)

  const { isOverflowing: isButtonContainerOverflowing } =
    useParentOverflowDetection({
      childRef: buttonContainerRef,
      parentRef: tableContainerRef as React.RefObject<HTMLElement>,
      threshold: 24
    })

  const { selectAndLink, cancelSelectAndLink, shouldRenderSelectAndLink } =
    useSelectAndLinkIframeBridge()

  const {
    isOpen: isOpenInformationalDocumentUpload,
    close: closeInformationalDocumentUpload,
    open: openInformationalDocumentUpload
  } = useIsOpen()

  const { displayUserError } = useDisplayUserError()

  const [_, setRefetchObjectView] = useSiteState<boolean>('refetch-objectview')

  const [searchParams] = useSearchParams()
  const isUniselection = searchParams.has('uniselection')

  const save = useTableActionSave({
    initialStatusCode: initialStatus?.code || ''
  })

  const onDocumentUploadSuccess = async () => {
    setRefetchObjectView(true)
  }

  const onSave = async () => {
    const result = await save()

    const isSuccessful = result?.every((obj) => obj?.success)

    if (isSuccessful) {
      feedbackcard(t('data-tables.success'), {
        type: 'success',
        message: t('data-tables.success')
      })

      setRefetchObjectView(true)

      return
    }

    const error = result?.find((obj) => !obj?.success)

    if (!error || error?.message) {
      displayUserError({
        error,
        title: t('data-tables.error'),
        userMessageFallback: t('data-tables.error')
      })
    }
  }

  //Temporary function
  const placeholderAction = () => undefined

  const getFormattedSelectedRows = (): SelectAndLinkValue[] =>
    Object.values(selectedRows).map((row) => row.value ?? { uuid: row.uuid })

  const selectAndLinkAction = () => {
    const formattedSelectedRows = getFormattedSelectedRows()
    deselectAllRows()

    selectAndLink(formattedSelectedRows)
  }

  const cancel = () => {
    setMode('view')
    setChangedFieldValues({})
  }

  const formattedExtraSelectionActions = extraSelectionActions?.map(
    (action) => ({
      ...action,
      onClick: () => {
        action.onClick?.(getFormattedSelectedRows())
        deselectAllRows()
      },
      disabled: Object.keys(selectedRows).length === 0
    })
  )

  const selectionActionsOptions = useMemo((): SelectionAction[] => {
    const baseActions = [
      primarySelectionAction || {
        children: t('data-tables.selection-actions'),
        onClick: placeholderAction,
        impactClass: 'situationChange' as const
      }
    ]

    if (insertOnly) {
      return baseActions
    }

    if (!hideEditAction) {
      baseActions.push({
        children: t('data-tables.modify'),
        onClick: () => setMode('edit'),
        impactClass: 'situationChange' as const
      })
    }

    return [...baseActions, ...formattedExtraSelectionActions]
  }, [
    primarySelectionAction,
    t,
    insertOnly,
    hideEditAction,
    formattedExtraSelectionActions,
    setMode
  ])

  const disableSelectionActions =
    selectionActionsOptions.length < 2 && !primarySelectionAction

  const buttonContainer =
    mode === 'edit' || mode === 'insert' ? (
      <div className='col-12'>
        <ActionsList>
          <Button
            onClick={onSave}
            variant='primary'
            impactClass='situationChange'
            heightClass='semiSmall'
          >
            {t('data-tables.save-and-activate')}
          </Button>

          <Button onClick={cancel} variant='secondary' heightClass='semiSmall'>
            {t('data-tables.cancel')}
          </Button>
        </ActionsList>
      </div>
    ) : (
      <ButtonContainer ref={buttonContainerRef}>
        <DocumentListCard>
          <ButtonList
            data-testid='button-list'
            $isOverflowing={isButtonContainerOverflowing}
          >
            {SHOW_SELECT_DESELECT && (
              <>
                <Button
                  heightClass='semiSmall'
                  onClick={placeholderAction}
                  variant='secondary'
                  disabled
                >
                  {t('data-tables.select')}
                </Button>
                <Button
                  heightClass='semiSmall'
                  onClick={placeholderAction}
                  variant='secondary'
                  disabled
                >
                  {t('data-tables.deselect')}
                </Button>
              </>
            )}
            {!isUniselection && (
              <>
                <Button
                  heightClass='semiSmall'
                  onClick={selectAllRows}
                  variant='secondary'
                  disabled={insertOnly}
                >
                  {t('data-tables.select-all')}
                </Button>
                <Button
                  heightClass='semiSmall'
                  onClick={deselectAllRows}
                  variant='secondary'
                  disabled={
                    Object.keys(selectedRows).length === 0 || insertOnly
                  }
                >
                  {t('data-tables.deselect-all')}
                </Button>
              </>
            )}

            {!hideInsertAction && <InsertButton />}

            <SelectAndLinkButton
              {...{
                disableLinkButton,
                hideSelectAndLinkAction,
                selectAndLinkOptions,
                selectAndLinkTargetPath,
                objectClassTitle: objectClassTitle ?? objectclass?.des
              }}
              objectclassUuid={objectclass?.uuid}
            />

            <SyntheticButton
              heightClass='semiSmall'
              options={selectionActionsOptions}
              dropdownLabel='more options'
              disabled={disableSelectionActions}
            />

            {!disableLinkButton && shouldRenderSelectAndLink && (
              <>
                <Button
                  variant='primary'
                  heightClass='semiSmall'
                  onClick={selectAndLinkAction}
                >
                  {t('data-tables.link')}
                </Button>

                <Button
                  variant='secondary'
                  heightClass='semiSmall'
                  onClick={cancelSelectAndLink}
                >
                  {t('data-tables.cancel')}
                </Button>
              </>
            )}

            {shouldRenderDocumentUploadAction && (
              <IconWrapper>
                <IconButton
                  iconName='Upload'
                  heightClass='small'
                  onClick={openInformationalDocumentUpload}
                  title={t('data-tables.document-upload')}
                  containerStyles={{ display: 'block' }}
                  style={{ width: '32px', height: '32px' }}
                />
              </IconWrapper>
            )}
          </ButtonList>
        </DocumentListCard>
      </ButtonContainer>
    )

  return (
    <>
      {shouldRenderDocumentUploadAction && (
        <InformationalDocumentUpload
          isOpen={isOpenInformationalDocumentUpload}
          onClose={closeInformationalDocumentUpload}
          onSuccess={onDocumentUploadSuccess}
        />
      )}
      <Container>{buttonContainer}</Container>
    </>
  )
}

export default TableActions
